/**
 * Extraction intelligente du matricule / nom depuis un contenu scanné.
 *
 * - Code-barres 1D de la carte étudiant : contient directement le matricule
 *   (ex: "222IB0722", "2I4552442", "L1S1-0093", ...)
 * - QR code du reçu (généré par système externe / SAGE) : contient un texte
 *   structuré ou libre. On tente d'y trouver un matricule via regex ;
 *   sinon on retombe sur les mots probablement en majuscules.
 */

export interface ExtractionResult {
  /** Matricule normalisé si trouvé, sinon null. */
  matricule: string | null;
  /** Texte de recherche à défaut de matricule (ex: nom+prénom). */
  searchQuery: string | null;
  /** Contenu brut du scan (utile en debug ou pour affichage). */
  raw: string;
  /** Source de la déduction : 'direct' (contenu = matricule), 'regex_qr', 'nom_qr'. */
  source: 'direct' | 'regex_qr' | 'nom_qr' | 'unknown';
}

// Motifs de matricules observés à l'Institut 2i :
//   - 222IB0043   (222 = pôle Commerce Brazzaville, IB = code interne, ....)
//   - 2I4552442   (ancien format)
//   - 24L1MI001   (année + niveau + option + numéro)
//   - 25M2CS042
// Contrainte : lettres majuscules et chiffres, longueur 6-16.
const MATRICULE_REGEX = /\b([A-Z0-9]{6,16})\b/g;

// Faux positifs à écarter (mots courants en majuscules parfois vus dans les QR)
const NOISE = new Set([
  'RECU', 'PAIEMENT', 'OFFICIEL', 'INSTITUT', 'INTERNATIONAL',
  'POLYTECHNIQUE', 'COMMERCE', 'JURIDIQUE', 'BRAZZAVILLE', 'POINTE',
  'NOIRE', 'CAISSE', 'MONTANT', 'TOTAL', 'RESTE', 'FCFA', 'CFA',
  'BOPAKA', 'BATIGNOLLES', 'AVENUE', 'RUE', 'BASE',
]);

function looksLikeMatricule(s: string): boolean {
  if (!s) return false;
  if (s.length < 6 || s.length > 16) return false;
  if (!/[A-Z]/.test(s) && !/^\d+$/.test(s)) return false; // au moins 1 lettre OU tout chiffres
  if (NOISE.has(s.toUpperCase())) return false;
  // Doit contenir au moins 3 chiffres pour éviter des mots type "STAGE"
  const digits = (s.match(/\d/g) || []).length;
  if (digits < 3) return false;
  return true;
}

/**
 * Strip AIM (Association for Automatic Identification and Mobility) prefix.
 *
 * Certains scanners préfixent le contenu décodé avec un identifier standard :
 *   `]C0`, `]C1` : Code 128 (]C1 = avec FNC1, typique GS1)
 *   `]Q0`..`]Q6` : QR code
 *   `]I0` : ITF
 *   `]E0` : EAN
 *   `]F0` : Codabar
 *   `]d1` : Data Matrix
 *
 * Sans ce strip, un matricule "222IB0722" arrive comme "]C1222IB0722" et
 * le parsing casse. Format AIM = "]" + 1 lettre + 1 chiffre.
 */
function stripAimPrefix(s: string): string {
  return s.replace(/^\][A-Za-z][0-9]/, '');
}

export function extractCode(raw: string): ExtractionResult {
  const trimmed = stripAimPrefix((raw || '').trim());
  if (!trimmed) {
    return { matricule: null, searchQuery: null, raw: '', source: 'unknown' };
  }

  const upper = trimmed.toUpperCase();

  // 1. Contenu direct = matricule (cas typique du code-barres 1D)
  if (looksLikeMatricule(upper) && !/\s/.test(trimmed)) {
    return {
      matricule: upper,
      searchQuery: null,
      raw: trimmed,
      source: 'direct',
    };
  }

  // 2. QR structuré : chercher le champ Matricule dans un JSON ou clé:valeur
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object') {
      const mat = parsed.matricule || parsed.Matricule || parsed.MATRICULE || parsed.mat;
      if (typeof mat === 'string' && looksLikeMatricule(mat.toUpperCase())) {
        return {
          matricule: mat.toUpperCase(),
          searchQuery: null,
          raw: trimmed,
          source: 'regex_qr',
        };
      }
      const nom = parsed.nom || parsed.Nom || parsed.NOM;
      const prenoms = parsed.prenoms || parsed.Prenoms || parsed.prenom;
      if (nom) {
        return {
          matricule: null,
          searchQuery: `${nom} ${prenoms || ''}`.trim(),
          raw: trimmed,
          source: 'nom_qr',
        };
      }
    }
  } catch { /* pas du JSON */ }

  // 3. QR texte libre : regex "Matricule : XXX" ou "Mat: XXX"
  const matField = trimmed.match(/matricule\s*[:=]\s*([A-Z0-9]{6,16})/i);
  if (matField && looksLikeMatricule(matField[1].toUpperCase())) {
    return {
      matricule: matField[1].toUpperCase(),
      searchQuery: null,
      raw: trimmed,
      source: 'regex_qr',
    };
  }

  // 3b. Format des reçus 2i (imprimés par système externe) :
  //     "N° reçu: 13018\nTitulaire: MABAYA Graciace Divine\nClasse: L2TL"
  //     On retourne toute la ligne titulaire comme searchQuery — le backend
  //     supporte la recherche multi-mots (chaque token match nom OU prenoms
  //     via AND), donc "MABAYA Graciace Divine" trouve bien l'étudiant.
  const titulaire = trimmed.match(/titulaire\s*[:=]\s*(.+?)(?:\r?\n|$)/i);
  if (titulaire) {
    const line = titulaire[1].trim();
    if (line.length >= 2) {
      return {
        matricule: null,
        searchQuery: line.slice(0, 80),
        raw: trimmed,
        source: 'nom_qr',
      };
    }
  }

  // 4. Fallback : chercher n'importe quel token qui ressemble à un matricule
  const tokens = upper.match(MATRICULE_REGEX) || [];
  const candidat = tokens.find(looksLikeMatricule);
  if (candidat) {
    return {
      matricule: candidat,
      searchQuery: null,
      raw: trimmed,
      source: 'regex_qr',
    };
  }

  // 5. Aucun matricule → extraire "Nom : XXX" ou "Prénom : XXX"
  const nomField = trimmed.match(/nom\s*[:=]\s*([A-Za-zÀ-ÿ' -]{2,})/i);
  const prenomField = trimmed.match(/pr[eé]nom(?:s)?\s*[:=]\s*([A-Za-zÀ-ÿ' -]{2,})/i);
  if (nomField || prenomField) {
    const q = [nomField?.[1], prenomField?.[1]].filter(Boolean).join(' ').trim();
    if (q.length >= 2) {
      return {
        matricule: null,
        searchQuery: q,
        raw: trimmed,
        source: 'nom_qr',
      };
    }
  }

  // 6. Dernier recours : renvoyer le contenu brut comme query (max 40 chars)
  return {
    matricule: null,
    searchQuery: trimmed.slice(0, 40),
    raw: trimmed,
    source: 'unknown',
  };
}
